import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from "react";
import { createUnassignedFile, getUnassignedFiles, deleteUnassignedFile, uploadFile, getInsightDefaultBoard, setInsightDefaultBoard as setInsightDefaultBoardApi, getUserBoardsList } from "@/network/network";
import { toast } from "sonner";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord } from "@/types/entity";
import { getFileExtension } from "@/modern/utils";
import { getFormattedBoardData } from "@/other/utils/utils";

export interface UnassignedFile {
  id: number;
  document_id: number;
  url: string;
  thumbnail_url: string;
  creator: number;
  name: string;
  created_at: string;
  size: number;
  /** Present when API returns it; used for UI affordance only */
  insight_count?: number;
}

export interface InsightBoard {
  id: number;
  display_name: string;
  has_ai_enabled?: boolean;
  [key: string]: unknown;
}

export type FileType = "all" | "images" | "videos" | "documents" | "audio";
export type SortOption = "name" | "date" | "type" | "size";
export type ViewMode = "grid" | "list";

export type InsightSetupState = null | { mode: "no-ai-boards" };

interface MyMediaContextValue {
  files: UnassignedFile[];
  totalFilesCount: number;
  selectedFiles: Set<number>;
  isLoading: boolean;
  isAssigning: boolean;
  searchQuery: string;
  fileTypeFilter: FileType;
  sortOption: SortOption;
  viewMode: ViewMode;
  setSearchQuery: (query: string) => void;
  setFileTypeFilter: (type: FileType) => void;
  setSortOption: (option: SortOption) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleFileSelection: (fileId: number) => void;
  selectAllFiles: () => void;
  clearSelection: () => void;
  fetchUnassignedFiles: () => Promise<void>;
  uploadAndCreateUnassignedFile: (file: File) => Promise<void>;
  deleteUnassignedFileById: (id: number) => Promise<void>;
  assignFilesToEntity: (fileIds: number[], entityRecord: EntityRecord) => Promise<void>;
  entityRecord: EntityRecord | null;
  setEntityRecord: (record: EntityRecord | null) => void;
  fileTypeCounts: Record<FileType, number>;
  defaultBoardId: number | null;
  setDefaultBoard: (boardId: number) => Promise<boolean>;
  aiEnabledBoards: InsightBoard[];
  isLoadingBoards: boolean;
  insightDialogFile: UnassignedFile | null;
  insightSetup: InsightSetupState;
  beginInsightFlow: (file: UnassignedFile) => void;
  dismissInsightSetup: () => void;
  openInsightDialog: (file: UnassignedFile) => void;
  closeInsightDialog: () => void;
}

const MyMediaContext = createContext<MyMediaContextValue | undefined>(undefined);

export const useMyMediaContext = () => {
  const context = useContext(MyMediaContext);
  if (!context) {
    throw new Error("useMyMediaContext must be used within MyMediaProvider");
  }
  return context;
};

interface MyMediaProviderProps {
  children: ReactNode;
  initialEntityRecord?: EntityRecord | null;
}

export const MyMediaProvider = ({ children, initialEntityRecord = null }: MyMediaProviderProps) => {
  const { project } = useSwivuBryntumContext();
  const [files, setFiles] = useState<UnassignedFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState<FileType>("all");
  const [sortOption, setSortOption] = useState<SortOption>("name");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [entityRecord, setEntityRecord] = useState<EntityRecord | null>(initialEntityRecord);

  const [defaultBoardId, setDefaultBoardId] = useState<number | null>(null);
  const [aiEnabledBoards, setAiEnabledBoards] = useState<InsightBoard[]>([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [insightDialogFile, setInsightDialogFile] = useState<UnassignedFile | null>(null);
  const [insightSetup, setInsightSetup] = useState<InsightSetupState>(null);

  const extractFileName = useCallback((url: string, id: number): string => {
    try {
      const urlParts = url.split("/");
      const fileName = urlParts[urlParts.length - 1];
      const cleanName = fileName.split("?")[0];
      if (cleanName.includes(".") && cleanName.length > 5) {
        return decodeURIComponent(cleanName);
      }
    } catch (e) {
      // Fallback
    }
    return `file-${id}`;
  }, []);

  const getFileTypeCategory = useCallback((file: UnassignedFile): FileType => {
    const fileName = file.name || extractFileName(file.url, file.id);
    const ext = getFileExtension(fileName).toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(ext)) {
      return "images";
    }
    if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(ext)) {
      return "videos";
    }
    if (["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(ext)) {
      return "audio";
    }
    return "documents";
  }, [extractFileName]);

  useEffect(() => {
    if (!entityRecord) {
      setSelectedFiles(new Set());
    }
  }, [entityRecord]);

  // Fetch the default board preference and list of AI-enabled boards
  useEffect(() => {
    const loadBoardData = async () => {
      setIsLoadingBoards(true);
      try {
        const [boardPrefResp, boardsResp] = await Promise.all([
          getInsightDefaultBoard().catch(() => ({ data: { board_id: null } })),
          getUserBoardsList(),
        ]);

        setDefaultBoardId(boardPrefResp.data?.board_id ?? null);

        const allBoards = (boardsResp.data || []).map((b: Record<string, unknown>) => getFormattedBoardData(b));
        const aiBoards = allBoards.filter((b: InsightBoard) => b.has_ai_enabled === true);
        setAiEnabledBoards(aiBoards);
      } catch (error) {
        console.error("Failed to load board data for insights:", error);
      } finally {
        setIsLoadingBoards(false);
      }
    };
    loadBoardData();
  }, []);

  const setDefaultBoard = useCallback(async (boardId: number): Promise<boolean> => {
    try {
      await setInsightDefaultBoardApi(boardId);
      setDefaultBoardId(boardId);
      toast.success("Default insight board updated");
      return true;
    } catch (error) {
      console.error("Failed to set default insight board:", error);
      toast.error("Failed to update default board");
      return false;
    }
  }, []);

  const openInsightDialog = useCallback((file: UnassignedFile) => {
    setInsightDialogFile(file);
  }, []);

  const dismissInsightSetup = useCallback(() => {
    setInsightSetup(null);
  }, []);

  const beginInsightFlow = useCallback(
    (file: UnassignedFile) => {
      if (isLoadingBoards) {
        toast.info("Still loading boards. Try again in a moment.");
        return;
      }
      if (aiEnabledBoards.length === 0) {
        setInsightSetup({ mode: "no-ai-boards" });
        return;
      }
      openInsightDialog(file);
    },
    [isLoadingBoards, aiEnabledBoards.length, openInsightDialog]
  );

  const closeInsightDialog = useCallback(() => {
    setInsightDialogFile(null);
  }, []);

  const fetchUnassignedFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await getUnassignedFiles();
      const filesWithNames = (data || []).map((file: UnassignedFile) => ({
        ...file,
        name: file.name || extractFileName(file.url, file.id),
      }));
      setFiles(filesWithNames);
    } catch (error) {
      console.error("Failed to fetch unassigned files:", error);
      toast.error("Failed to load files. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [extractFileName]);

  const uploadAndCreateUnassignedFile = useCallback(async (file: File) => {
    try {
      const { data: uploadData } = await uploadFile(file, file.name);
      const documentId = uploadData.id;
      await createUnassignedFile(documentId);
      await fetchUnassignedFiles();
    } catch (error) {
      console.error("Failed to upload file:", error);
      throw error;
    }
  }, [fetchUnassignedFiles]);

  const deleteUnassignedFileById = useCallback(async (id: number) => {
    try {
      await deleteUnassignedFile(id);
      setFiles((prev) => prev.filter((f) => f.id !== id));
      setSelectedFiles((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("File deleted successfully");
    } catch (error) {
      console.error("Failed to delete file:", error);
      toast.error("Failed to delete file. Please try again.");
    }
  }, []);

  const assignFilesToEntity = useCallback(
    async (fileIds: number[], entityRecord: EntityRecord) => {
      if (!entityRecord || !project) {
        toast.error("No entity selected for assignment");
        return;
      }

      setIsAssigning(true);
      try {
        const selectedFileRecords = files.filter((f) => fileIds.includes(f.id));
        const documentIds = selectedFileRecords.map((f) => f.document_id);

        const records = documentIds.map((documentId) => ({
          documentId,
          [entityRecord.entityType]: entityRecord.id,
        }));

        project.boardFilesStore.add(records);

        await project.sync();

        await fetchUnassignedFiles();

        setSelectedFiles(new Set());

        const fileCount = fileIds.length;
        const fileText = fileCount === 1 ? "file" : "files";
        toast.success(`Successfully assigned ${fileCount} ${fileText} to ${entityRecord.name || "entity"}`);
      } catch (error) {
        console.error("Failed to assign files:", error);
        toast.error("Failed to assign files. Please try again.");
      } finally {
        setIsAssigning(false);
      }
    },
    [files, project, fetchUnassignedFiles]
  );

  const toggleFileSelection = useCallback((fileId: number) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const fileTypeCounts = useMemo(() => {
    const counts: Record<FileType, number> = {
      all: files.length,
      images: 0,
      videos: 0,
      documents: 0,
      audio: 0,
    };

    files.forEach((file) => {
      const category = getFileTypeCategory(file);
      if (category !== "all") {
        counts[category]++;
      }
    });

    return counts;
  }, [files, getFileTypeCategory]);

  const filteredFiles = useMemo(() => {
    let result = [...files];

    if (fileTypeFilter !== "all") {
      result = result.filter((file) => getFileTypeCategory(file) === fileTypeFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((file) => {
        const fileName = file.name || extractFileName(file.url, file.id);
        return fileName.toLowerCase().includes(query);
      });
    }

    result.sort((a, b) => {
      const nameA = (a.name || extractFileName(a.url, a.id)).toLowerCase();
      const nameB = (b.name || extractFileName(b.url, b.id)).toLowerCase();
      const typeA = getFileTypeCategory(a);
      const typeB = getFileTypeCategory(b);
      const extA = getFileExtension(a.name || extractFileName(a.url, a.id));
      const extB = getFileExtension(b.name || extractFileName(b.url, b.id));

      switch (sortOption) {
        case "name":
          return nameA.localeCompare(nameB);
        case "type":
          if (typeA !== typeB) {
            return typeA.localeCompare(typeB);
          }
          return extA.localeCompare(extB);
        case "date": {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA;
        }
        case "size": {
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          return sizeB - sizeA;
        }
        default:
          return 0;
      }
    });

    return result;
  }, [files, fileTypeFilter, searchQuery, sortOption, getFileTypeCategory, extractFileName]);

  const selectAllFiles = useCallback(() => {
    setSelectedFiles(new Set(filteredFiles.map((f) => f.id)));
  }, [filteredFiles]);

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  useEffect(() => {
    fetchUnassignedFiles();
  }, [fetchUnassignedFiles]);

  return (
    <MyMediaContext.Provider
      value={{
        files: filteredFiles,
        totalFilesCount: files.length,
        selectedFiles,
        isLoading,
        isAssigning,
        searchQuery,
        fileTypeFilter,
        sortOption,
        viewMode,
        setSearchQuery,
        setFileTypeFilter,
        setSortOption,
        setViewMode,
        toggleFileSelection,
        selectAllFiles,
        clearSelection,
        fetchUnassignedFiles,
        uploadAndCreateUnassignedFile,
        deleteUnassignedFileById,
        assignFilesToEntity,
        entityRecord,
        setEntityRecord,
        fileTypeCounts,
        defaultBoardId,
        setDefaultBoard,
        aiEnabledBoards,
        isLoadingBoards,
        insightDialogFile,
        insightSetup,
        beginInsightFlow,
        dismissInsightSetup,
        openInsightDialog,
        closeInsightDialog,
      }}
    >
      {children}
    </MyMediaContext.Provider>
  );
};
